import { centsPerDollar, producersKeep, TIER_LABEL, type Tier, type TierSplit as Split } from "../tiers";
import { CALCULATOR_PATH } from "./EarningsCalculator";

// Where every $1 of a pool goes, led by what the producer keeps. Cents per
// $1, never percents: a percent reads like a fee schedule, and the producer
// is reading this to decide whether to enter.

const LABEL = {
  backing: "Backing share",
  winner: "Winner's cut",
  platform: "Platform",
  fans: "Winning fans",
};
const CALCULATOR_LINK = "See what you could make this month →";

export default function TierSplit({ tier, split }: { tier: Tier; split: Split }) {
  const fansBps = Math.max(10_000 - split.rake_bps - split.producer_bps - split.backing_bps, 0);
  const segments = [
    { key: "backing", bps: split.backing_bps, bar: "bg-money", label: LABEL.backing },
    { key: "winner", bps: split.producer_bps, bar: "bg-money-deep", label: LABEL.winner },
    { key: "platform", bps: split.rake_bps, bar: "bg-chrome-dim", label: LABEL.platform },
    { key: "fans", bps: fansBps, bar: "bg-ground-edge", label: LABEL.fans },
  ] as const;

  return (
    <div data-testid="tier-split" className="mt-4 rounded-xl border border-ground-edge bg-ground-raised px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span
          data-testid="tier-badge"
          className="rounded-full border border-gold/40 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-gold"
        >
          {TIER_LABEL[tier]}
        </span>
        <p data-testid="tier-keep" className="text-[12px] font-bold text-money">
          {producersKeep(split)}
        </p>
      </div>

      <div data-testid="tier-bar" className="mt-3 flex h-2 w-full overflow-hidden rounded-full bg-ground">
        {segments.map((s) => (
          <span
            key={s.key}
            data-testid={`tier-seg-${s.key}`}
            aria-hidden
            style={{ width: `${s.bps / 100}%` }}
            className={s.bar}
          />
        ))}
      </div>

      <ul data-testid="tier-legend" className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1">
        {segments.map((s) => (
          <li key={s.key} className="text-[11px] text-chrome-dim">
            {s.label} <span className="bf-tabular font-bold text-chrome">{centsPerDollar(s.bps)}</span>
          </li>
        ))}
      </ul>

      <a
        data-testid="tier-calculator"
        href={CALCULATOR_PATH}
        className="bf-focusable mt-2.5 inline-block text-[11px] font-bold text-gold hover:text-gold-light"
      >
        {CALCULATOR_LINK}
      </a>
    </div>
  );
}
